import React, { useState } from 'react';
import { X, CreditCard, Loader } from 'lucide-react';
interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const BuyCreditsModal = ({
  isOpen,
  onClose
}: BuyCreditsModalProps) => {
  const [creditAmount, setCreditAmount] = useState(100);
  const [isProcessing, setIsProcessing] = useState(false);
  if (!isOpen) return null;
  const handleBuyCredits = async () => {
    setIsProcessing(true);
    try {
      // In production, this would call your backend to create a Stripe checkout session
      // For now, we'll simulate the flow
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          creditAmount,
          pricePerCredit: 1 // $1 per credit
        })
      });
      const {
        sessionUrl
      } = await response.json();
      // Redirect to Stripe checkout
      window.location.href = sessionUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setIsProcessing(false);
      // In production, show error toast/notification
    }
  };
  const totalPrice = creditAmount * 1; // $1 per credit
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-135">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" data-id="element-136">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-137">
          <div className="flex items-center gap-3" data-id="element-138">
            <div className="w-10 h-10 bg-[#6366f1]/10 rounded-lg flex items-center justify-center" data-id="element-139">
              <CreditCard className="w-5 h-5 text-[#6366f1]" data-id="element-140" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-141">Buy Credits</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={isProcessing} data-id="element-142">
            <X className="w-5 h-5" data-id="element-143" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-144">
          {/* Credit Amount Input */}
          <div data-id="element-145">
            <label htmlFor="creditAmount" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-146">
              Number of Credits
            </label>
            <div className="relative" data-id="element-147">
              <input id="creditAmount" type="number" min="1" max="10000" value={creditAmount} onChange={e => setCreditAmount(Math.max(1, parseInt(e.target.value) || 1))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none text-lg font-medium" disabled={isProcessing} data-id="element-148" />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500" data-id="element-149">
                credits
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500" data-id="element-150">
              1 credit = 1 prospect message
            </p>
          </div>

          {/* Quick Select Buttons */}
          <div className="grid grid-cols-4 gap-2" data-id="element-151">
            {[50, 100, 250, 500].map(amount => <button key={amount} onClick={() => setCreditAmount(amount)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${creditAmount === amount ? 'bg-[#6366f1] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} disabled={isProcessing} data-id="element-152">
                {amount}
              </button>)}
          </div>

          {/* Pricing Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-id="element-153">
            <div className="flex justify-between text-sm" data-id="element-154">
              <span className="text-gray-600" data-id="element-155">Credits</span>
              <span className="font-medium text-gray-900" data-id="element-156">{creditAmount}</span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-157">
              <span className="text-gray-600" data-id="element-158">Price per credit</span>
              <span className="font-medium text-gray-900" data-id="element-159">$1.00</span>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-160">
              <div className="flex justify-between" data-id="element-161">
                <span className="font-semibold text-gray-900" data-id="element-162">Total</span>
                <span className="font-bold text-xl text-[#6366f1]" data-id="element-163">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="flex items-start gap-2 text-xs text-gray-500" data-id="element-164">
            <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-165" />
            <p data-id="element-166">
              Secure payment powered by Stripe. Your credits will be added
              immediately after payment.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-3" data-id="element-167">
          <button onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors" disabled={isProcessing} data-id="element-168">
            Cancel
          </button>
          <button onClick={handleBuyCredits} disabled={isProcessing || creditAmount < 1} className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-lg hover:bg-[#5457e5] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-169">
            {isProcessing ? <>
                <Loader className="w-4 h-4 animate-spin" data-id="element-170" />
                Processing...
              </> : <>
                <CreditCard className="w-4 h-4" data-id="element-171" />
                Pay ${totalPrice.toFixed(2)}
              </>}
          </button>
        </div>
      </div>
    </div>;
};